import { Injectable } from '@angular/core';
import { GeneralUtilities } from '../utilities/generaleUtilities';
import { LogService } from './log.service';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { selectUserInformation } from '../state/selectors/user-profile.selectors';
import { firstValueFrom } from 'rxjs';
import { AgGridPageSizeSelectorComponent } from '../components/ag-grid-page-size-selector/ag-grid-page-size-selector.component';
import { CustomLoadingCellRenderer } from '../components/loading-cell-renderer/loading-cell-renderer.component';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  formatDate(value: any): string {
      throw new Error('Method not implemented.');
  }

  private currentBackgroundColorHex: string;
  private siteWideThemeBackgroundColorH =
    '--site-wide-theme-background-color-h';
  private siteWideThemebackgroundColorS =
    '--site-wide-theme-background-color-s';
  private siteWideThemebackgroundColorL =
    '--site-wide-theme-background-color-l';
  private siteWideThemeFontFamily = '--site-wide-theme-font-family';
  private siteWideThemeBlackColor = '--site-wide-theme-black-color';

  constructor(
    private logger: LogService,
    private store: Store<AppState>,
  ) {}

  async getVUoM(): Promise<string> {
    const uoM = await firstValueFrom(this.store.select(selectUserInformation));

    return 'gal';
  }

  public convertVolumeUoMValue(value: number, uoM: string): number {
    //returns the converted value from gallons to the uoM

    //if uoM is not provided, then it will return the value in gallons
    if (!value || value === 0 || !uoM) {
      return 0;
    }

    switch (uoM.toLowerCase()) {
      case 'gal':
        return value;
      case 'barrels':
        return value / 42;
      case 'liters':
        return value * 3.78541;
      case 'cubic feet':
        return value * 7.48052;
      case 'cubic meters':
        return value * 0.00378541;
      default:
        return value;
    }
  }

  public convertLevelUoMValue(value: number, uoM: string): number {
    //returns the converted value from inches to the uoM

    //if uoM is not provided, then it will return the value in inches
    if (!value) {
      return 0;
    }

    if (!uoM) {
      return value;
    }

    switch (uoM.toLowerCase()) {
      case 'in':
        return value;
      case 'ft':
        return value / 12;
      case 'cm':
        return value * 0.393701;
      case 'm':
        return value * 0.0328084;
      default:
        return value;
    }
  }
  loadingCellRenderer: any = CustomLoadingCellRenderer;
  loadingCellRendererParams: any = {
    loadingMessage: 'One moment please...',
  };

  getDefaultGridOptions(
    fileNameFn: () => string = () => 'excel.xlsx',
  ): import('@ag-grid-community/core').GridOptions {
    return {
      defaultExcelExportParams: {
        fileName: fileNameFn(),
      },
      defaultColDef: {
        enableCellChangeFlash: true,
        sortable: true,
        filter: true,
        resizable: true,
        width: 150,
      },
      //loadingCellRenderer: this.loadingCellRenderer,
      //loadingCellRendererParams: this.loadingCellRendererParams,
      enableRangeSelection: true,
      enableRangeHandle: true,
      suppressCellSelection: false,
      suppressClipboardPaste: false,
      suppressCopyRowsToClipboard: false,
      suppressCsvExport: true,
      rowClass: 'hover-row-effect',
      rowHeight: 24,
      pagination: true,
      paginationPageSize: 50,
      statusBar: {
        statusPanels: [
          {
            statusPanel: AgGridPageSizeSelectorComponent,
            key: 'agGridPageSizeSelector',
          },
        ],
      },
    };
  }

  public getSkybitzBlueColorValue(): string {
    return this.getCSSValue('--skybitz-blue');
  }

  public getTextWhiteColorValue(): string {
    return this.getCSSValue('--text-white');
  }

  public getSideNavWidth(): string {
    return this.getCSSValue('--left-nav-width');
  }

  public getSideNavWidthNarrow(): string {
    return this.getCSSValue('--left-nav-width-narrow');
  }

  public getSiteWideThemeFontFamily(): string {
    return this.getCSSValue(this.siteWideThemeFontFamily);
  }

  public getSideNavTop(): string {
    return this.getCSSValue('--left-nav-top');
  }

  public setSiteWideThemeFontFamily(fontFamilyName: string) {
    fontFamilyName = fontFamilyName ?? 'skybitzFont';
    this.setCSSValue(this.siteWideThemeFontFamily, fontFamilyName);
  }

  public setSiteWideThemeColor(hexColor: string) {
    try {
      if (hexColor === null || hexColor === undefined || hexColor === '') {
        return;
      }

      //Convert hex color to Hue, Saturation, Lighting
      // This is used in the themable CSS to provide support for the
      // HSL[a] methods that help to generate themes dynamically simply based on the
      //H/S/L instead of HEX

      //clean-up any white-spaces the hex color string may have
      hexColor = hexColor.replace(' ', '');
      let themeHSL = null;
      try {
        themeHSL = GeneralUtilities.hexToHSL(hexColor);
      } catch (error) {
        this.logger.error(
          'Error setSiteWideThemeColor ',
          hexColor,
          error.message,
        );
        return;
      }

      if (themeHSL === null || themeHSL === undefined) {
        let rgbValues = GeneralUtilities.colorNameToHex(hexColor);
        let regex =
          /rgb *\( *([0-9]{1,3}) *, *([0-9]{1,3}) *, *([0-9]{1,3}) *\)/;
        let values = regex.exec(rgbValues);
        if (values) {
          themeHSL = values;
        } else {
          return;
        }
      }

      this.currentBackgroundColorHex = hexColor;

      this.setCSSValue(this.siteWideThemeBackgroundColorH, themeHSL['h']);
      this.setCSSValue(this.siteWideThemebackgroundColorS, themeHSL['s']);
      this.setCSSValue(this.siteWideThemebackgroundColorL, themeHSL['l']);
    } catch (error) {
      this.logger.error(
        'Error setSiteWideThemeColor ',
        hexColor,
        error.message,
      );
    }
  }

  setSiteWideThemeBlackColor(hexColor: string) {
    this.setCSSValue(this.siteWideThemeBlackColor, hexColor);
  }

  getSiteWideThemeBackgroundColorHEXValue() {
    return this.currentBackgroundColorHex;
  }

  getSiteWideThemeDarkColorHEXValue() {
    return this.getCSSValue('--black-background-color');
  }

  getSiteWideThemeColorHEXValue() {
    return this.getCSSValue('--site-wide-theme-background-color');
  }

  getSiteWideThemeContrastColor() {
    let h = Number.parseInt(
      this.getCSSValue(this.siteWideThemeBackgroundColorH),
    );
    let s = Number.parseInt(
      this.getCSSValue(this.siteWideThemebackgroundColorS),
    );
    let l = Number.parseInt(
      this.getCSSValue(this.siteWideThemebackgroundColorL),
    );

    //flip full light / dark.
    if (l === 0 || l === 100) {
      l = 50;
    } else {
      h = h + 90 - 360;
      h = Math.max(h, -1 * h);
    }
    return GeneralUtilities.hslToHex(h, s, l);

    /*
      --ct: calc(var(--site-wide-theme-background-color-h) + 180deg - 360deg);
      --ctH: max(var(--ct), -1*var(--ct));

      --site-wide-theme-color-rotated: hsl(
        var(--ctH),
        var(--site-wide-theme-background-color-s),
        var(--site-wide-theme-background-color-l)
    );
  */
  }

  getGridModules() {
    return [
      ClientSideRowModelModule,
      MenuModule,
      ColumnsToolPanelModule,
      SetFilterModule,
      FiltersToolPanelModule,
      StatusBarModule,
      RangeSelectionModule,
      ClipboardModule,
      ExcelExportModule,
    ];
  }

  /**
   * Resolves a CSS custom property to its computed value, e.g. '--green-rgb' to
   * '0 171 78'. Callers that need a literal colour - anything handing one to a
   * chart, which cannot take a var() - read the token through here rather than
   * hardcoding its value. Returns '' when the stylesheet is not loaded.
   *
   * This reads computed style, so call it on a state change, never from a
   * template getter.
   */
  getCSSVariableValue(cssVariableName: string): string {
    return this.getCSSValue(cssVariableName);
  }

  /**
   * A declaration cannot break out of its rule through setProperty - the CSSOM parses the
   * value and drops it whole if it is malformed, so there is no ';' or '}' escape here. What
   * it will happily accept is url(), which turns a themed token into an outbound request, and
   * a comment sequence, which hides the rest of the value from review. Theme tokens are
   * colours, angles, percentages and a font name; none of them need any of that.
   */
  private static readonly unsafeCssValue = /[;{}<>\\]|url\s*\(|expression\s*\(|\/\*/i;

  private setCSSValue(cssVariableName: string, value: string) {
    if (value === null || value === undefined || UtilsService.unsafeCssValue.test(value)) {
      this.logger.error(
        'Rejected CSS custom property value ',
        cssVariableName,
        value,
      );
      return;
    }

    const r: any = document.querySelector(':root');
    r.style.setProperty(cssVariableName, value);
  }

  private getCSSValue(cssVariableName: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(cssVariableName)
      .trim();
  }


}
